import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { QUERY_KEY } from '../../../services/queryEndPoint';
import { Api } from '../../../services/interceptor';
import BreadCrumb from '../../../components/BreadCrumb';
import { Images } from '../../../utils/imageConstants';
import NoDataFound from '../../../components/common/NoDataFound';
import GoogleMap from './GoogleMap';

interface GroundDetails {
  groundId: string;
  groundName: string;
  groundAddress: string;
  email: string;
  imageUri: string | null;
  imageBucketLink: string | null;
  groundDescription: string;
  matchesPlayedTillDate: number;
  contactNumber: string;
  groundFacilities: string;
  daysOpen: string | null;
  timingOpen: string;
  canEdit: boolean;
  gmapLink: string | null;
  established?: string | null;
  capacity?: string | null;
}

const GroundDetails: React.FC = () => {
  const { groundId } = useParams<{ groundId: string }>();
  const navigate = useNavigate();

  const { data: groundDetails, isLoading } = useQuery<GroundDetails>({
    queryKey: [QUERY_KEY.getGroundDetails, groundId],
    enabled: !!groundId,
    queryFn: async () => {
      if (!groundId) return null;
      const response = await Api.get<GroundDetails>(
        `${QUERY_KEY.getGroundDetails}/${groundId}`
      );
      return response?.data || null;
    },
  });

  // Get ground image URL
  const getGroundImage = (): string => {
    if (!groundDetails) return Images.ground_image;
    const cdnBaseUrl = import.meta.env.VITE_IMAGE_CDN_BASE_URL;
    if (groundDetails?.imageUri) {
      if (cdnBaseUrl) {
        const baseUrl = cdnBaseUrl.endsWith('/') ? cdnBaseUrl.slice(0, -1) : cdnBaseUrl;
        const imagePath = groundDetails.imageUri.startsWith('/') ? groundDetails.imageUri : `/${groundDetails.imageUri}`;
        return `${baseUrl}${imagePath}`;
      }
      return groundDetails.imageUri;
    }
    return Images.ground_image;
  };

  if (isLoading) {
    return (
      <div className="ground-details-loading">
        <div>Loading...</div>
      </div>
    );
  }

  if (!groundDetails) {
    return (
      <div className="ground-details-wrapper">
        <BreadCrumb />
        <NoDataFound message="GROUND NOT FOUND" />
      </div>
    );
  }

  return (
    <div className="ground-details-wrapper">
      <div className="custom-container">
        <BreadCrumb />
        <div className="ground-details-container">
        {/* Back Button */}
        <button 
          className="ground-back-button"
          onClick={() => navigate(-1)}
          aria-label="Go back to grounds list"
        >
          <img src={Images.white_back_arrow} alt="Back" />
        </button>

        {/* Ground Header Info */}
        <div className="ground-header-info">
          <h1 className="ground-title">{groundDetails.groundName.toUpperCase()}</h1>
          <div className="ground-meta-info">
            <p className="ground-meta-item">
              <span className="meta-label-bold">Established:</span>
              <span className="meta-value">{groundDetails.established || 'N/A'}</span>
            </p>
            <div className="ground-meta-divider"></div>
            <p className="ground-meta-item">
              <span className="meta-label-bold">Capacity:</span>
              <span className="meta-value">{groundDetails.capacity || 'N/A'}</span>
            </p>
          </div>
        </div>

        {/* White Content Container */}
        <div className="ground-content-container">
          {/* Decorative background elements */}
          {/* <div className="ground-decorative-shape decorative-shape-1"></div>
          <div className="ground-decorative-shape decorative-shape-2"></div> */}

          {/* Ground Image Section */}
          <div className="ground-image-section">
            <img 
              src={getGroundImage()} 
              alt={groundDetails.groundName}
              className="ground-main-image"
            />
          </div>

          {/* Map Section */}
          <div className="ground-map-section">
            <h2 className="map-title">Map</h2>
            <div className="map-container">
              {groundDetails.groundAddress ? (
                <GoogleMap 
                  address={groundDetails.groundAddress}
                  gmapLink={groundDetails.gmapLink}
                />
              ) : (
                <div className="map-placeholder">
                  Map not available
                </div>
              )}
            </div>
            <p className="ground-address-text">{groundDetails.groundAddress}</p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default GroundDetails;
